/* ===== React Router Setup ===== */

import type { ReactNode } from 'react'
import { createHashRouter, Navigate } from 'react-router-dom'
import type { OrgModuleKey } from '@zetyar/domain'
import { MainLayout } from '@/presentation/components/layout/MainLayout'
import { RequireModule } from '@/presentation/components/layout/RequireModule'
import { RequireSuperAdmin } from '@/presentation/components/layout/RequireSuperAdmin'
import { LoginPage } from '@/presentation/pages/auth/LoginPage'
import { ForgotPasswordPage } from '@/presentation/pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/presentation/pages/auth/ResetPasswordPage'
import { AcceptInvitePage } from '@/presentation/pages/invitations/AcceptInvitePage'
import { DashboardPage } from '@/presentation/pages/dashboard/DashboardPage'
import { AnnouncementsPage } from '@/presentation/pages/announcements/AnnouncementsPage'
import { MembersPage } from '@/presentation/pages/members/MembersPage'
import { EventsPage } from '@/presentation/pages/events/EventsPage'
import { ProjectsPage } from '@/presentation/pages/projects/ProjectsPage'
import { ChatPage } from '@/presentation/pages/chat/ChatPage'
import { DocumentsPage } from '@/presentation/pages/documents/DocumentsPage'
import { CampaignsPage } from '@/presentation/pages/campaigns/CampaignsPage'
import { FinancePage } from '@/presentation/pages/finance/FinancePage'
import { DuesPage } from '@/presentation/pages/dues/DuesPage'
import { AgreementsPage } from '@/presentation/pages/agreements/AgreementsPage'
import { SalesPage } from '@/presentation/pages/sales/SalesPage'
import { ProfilePage } from '@/presentation/pages/profile/ProfilePage'
import { SettingsPage } from '@/presentation/pages/settings/SettingsPage'
import { AdminSaasPage } from '@/presentation/pages/admin/AdminSaasPage'
import { AuditPage } from '@/presentation/pages/audit/AuditPage'
import { RequestsPage } from '@/presentation/pages/requests/RequestsPage'
import { NotificationsPage } from '@/presentation/pages/notifications/NotificationsPage'
import { LandingPage } from '@/presentation/pages/landing/LandingPage'

function moduleRoute(path: string, module: OrgModuleKey, element: ReactNode) {
  return { path, element: <RequireModule module={module}>{element}</RequireModule> }
}

export const router = createHashRouter([
  {
    // Public promotional landing. Signed-in users see an "Ir a mi panel" button instead of being
    // redirected, so the page stays reachable for them too.
    path: '/',
    element: <LandingPage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/accept-invite',
    element: <AcceptInvitePage />,
  },
  {
    // Pathless layout: the authenticated app keeps its /dashboard, /members... URLs.
    element: <MainLayout />,
    children: [
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      moduleRoute('announcements', 'announcements', <AnnouncementsPage />),
      moduleRoute('members', 'members', <MembersPage />),
      moduleRoute('events', 'events', <EventsPage />),
      moduleRoute('projects', 'projects', <ProjectsPage />),
      moduleRoute('chat', 'chat', <ChatPage />),
      moduleRoute('documents', 'documents', <DocumentsPage />),
      moduleRoute('campaigns', 'campaigns', <CampaignsPage />),
      moduleRoute('finance', 'finance', <FinancePage />),
      moduleRoute('dues', 'dues', <DuesPage />),
      moduleRoute('agreements', 'agreements', <AgreementsPage />),
      moduleRoute('sales', 'sales', <SalesPage />),
      {
        path: 'supplies',
        element: <Navigate to="/agreements" replace />,
      },
      {
        path: 'audit',
        element: <AuditPage />,
      },
      moduleRoute('requests', 'requests', <RequestsPage />),
      {
        path: 'notifications',
        element: <NotificationsPage />,
      },
      {
        path: 'profile',
        element: <ProfilePage />,
      },
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      {
        path: 'admin/saas',
        element: (
          <RequireSuperAdmin>
            <AdminSaasPage />
          </RequireSuperAdmin>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])
